import { createContext, createElement, useContext, useMemo, type ReactNode } from 'react';
import { useParams } from 'react-router-dom';
import { useApiGet } from './useApiGet.js';
import { tenantBasePath } from './tenant.js';
import type { OwnProfileResponse } from './types/membership.js';
import {
  evaluateCapabilities,
  DENY_ALL_CAPABILITIES,
  type UserCapabilities,
} from './capabilities-eval.js';

export { evaluateCapabilities, DENY_ALL_CAPABILITIES, type UserCapabilities };

const CapabilitiesContext = createContext<UserCapabilities | null>(null);

export function CapabilitiesProvider({
  profile,
  children,
}: {
  profile?: OwnProfileResponse | null;
  children: ReactNode;
}) {
  const value = useMemo(() => evaluateCapabilities(profile), [profile]);
  return createElement(CapabilitiesContext.Provider, { value }, children);
}

/**
 * Shared hook for capability gating across client views (S4/GAP-PP-01).
 * When rendered under AuthenticatedShell, reads directly from context (no extra HTTP requests).
 * When rendered standalone (unit tests / isolated views), fetches /api/v1/me if slug is available.
 */
export function useCapabilities(explicitSlug?: string): UserCapabilities {
  const context = useContext(CapabilitiesContext);
  const routeParams = useParams<{ slug?: string }>();
  const slug = explicitSlug ?? routeParams.slug;

  const fallbackMe = useApiGet<OwnProfileResponse>(
    !context && slug ? `${tenantBasePath(slug)}/api/v1/me` : null,
    [slug],
  );

  const fallbackCaps = useMemo(
    () =>
      fallbackMe.status === 'ready' ? evaluateCapabilities(fallbackMe.data) : DENY_ALL_CAPABILITIES,
    [fallbackMe],
  );

  if (context) {
    return context;
  }

  return fallbackCaps;
}
